import type { SearchSubmitOptions } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import {
  createAgenticConversationTurn,
  createCompletedAgenticConversationTurn,
  createErroredAgenticConversationTurn,
  hasAgenticConversationEvents,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/agenticConversation'
import type { AgenticDocumentSearchResult } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type {
  AgenticConversationTurn,
  AgenticSearchConversation,
  DocumentSearchMode,
} from 'features/knowledgeDocumentViewer/types'
import React, { useCallback, useMemo } from 'react'

export interface UseAgenticSearchHeaderStateParams {
  conversation: AgenticSearchConversation | undefined
  result: AgenticDocumentSearchResult | undefined
  isLoadingDocuments: boolean
  query: string
  siteId: string
  searchMode: DocumentSearchMode
  onSearchSubmit: (options?: SearchSubmitOptions) => void
  onSearchKeyDown: (event: React.KeyboardEvent) => void
}

export interface UseAgenticSearchHeaderStateResult {
  turns: AgenticConversationTurn[]
  onAgenticSearchSubmit: () => void
  onAgenticSearchKeyDown: (event: React.KeyboardEvent) => void
}

export function useAgenticSearchHeaderState({
  conversation,
  result,
  isLoadingDocuments,
  query,
  siteId,
  searchMode,
  onSearchSubmit,
  onSearchKeyDown,
}: UseAgenticSearchHeaderStateParams): UseAgenticSearchHeaderStateResult {
  const shouldSubmitAgenticTurn = searchMode === 'agentic'
  const turns = useMemo<AgenticConversationTurn[]>(() => {
    if (
      conversation?.turns.length &&
      conversation.turns.every(hasAgenticConversationEvents)
    ) {
      return conversation.turns
    }
    const trimmedQuery = query.trim()
    if (!trimmedQuery) return []
    if (result && result.query === query) {
      return [
        createCompletedAgenticConversationTurn({
          turnId: 'agentic-current-turn',
          result,
        }),
      ]
    }
    return [
      isLoadingDocuments
        ? createAgenticConversationTurn({
            turnId: 'agentic-current-turn',
            query: trimmedQuery,
          })
        : createErroredAgenticConversationTurn({
            turnId: 'agentic-current-turn',
            query: trimmedQuery,
            siteId,
          }),
    ]
  }, [conversation, result, isLoadingDocuments, query, siteId])

  const onAgenticSearchSubmit = useCallback(() => {
    onSearchSubmit({
      clearInputAfterSubmit: shouldSubmitAgenticTurn,
      startAgenticTurn: shouldSubmitAgenticTurn,
    })
  }, [onSearchSubmit, shouldSubmitAgenticTurn])

  const onAgenticSearchKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.stopPropagation()
        onSearchSubmit({
          clearInputAfterSubmit: shouldSubmitAgenticTurn,
          startAgenticTurn: shouldSubmitAgenticTurn,
        })
        return
      }
      onSearchKeyDown(event)
    },
    [onSearchKeyDown, onSearchSubmit, shouldSubmitAgenticTurn]
  )

  return {
    turns,
    onAgenticSearchSubmit,
    onAgenticSearchKeyDown,
  }
}
